import { FilterInputType, InputPropOption, InputProps } from '@shared/components/filters-bar/input-props';
import { AvailabilityColumns, DataType } from '@miq/catalog';
import { DataObject } from '@shared/models/data-object.model';
// Type-only: `FiltersBarComponent` appears here exclusively as a parameter type
// (refreshGeographicalFilters / restoreValidSelections / buildLevelControlOptions'
// applyCascadeChanges). A value import created a runtime CYCLE — filters-bar.component ->
// view-spaces-from-tim.service -> filters.ts -> filters-bar.component — which forced the
// whole TIM graph into every bundle that renders a filters bar. `import type` is erased by
// the compiler, so the edge disappears at runtime while the typing is unchanged.
import type { FiltersBarComponent } from '@shared/components/filters-bar/filters-bar.component';
import { IOption } from '@shared/models/icommons.model';
import { ISearchFilterSettings } from '@shared/models/isearch-filter-settings.model';

export const inputIdentifiers = {
  marketIdentifier: 'MARKET_IDENTIFIER',
  submarketIdentifier: 'SUBMARKET_IDENTIFIER',
  districtIdentifier: 'DISTRICT_IDENTIFIER',
  neighbourhoodIdentifier: 'NEIGHBOURHOOD_IDENTIFIER',
  migTimLeasePropertyPurchase: 'MIQ_TIM_LEASE_PURCHASE',
  cbreInstructions: 'CBRE_INSTRUCTIONS',
  marketStatus: 'MARKET_STATUS',
  availabilityType: 'AVAILABILITY_TYPE',
  spaceUsageType: 'SPACE_USAGE_TYPE',
  spaceUsageSubType: 'SPACE_USAGE_SUB_TYPE',
  leaseType: 'LEASE_TYPE',
  grade: 'GRADE',
  smokeExtractionUnit: 'SMOKE_EXTRACTION_UNIT',
  availableSpace: 'AVAILABLE_SPACE',
  askingRentYearly: 'ASKING_RENTAL_RATE_YEARLY',
  minTerm: 'MINIMUM_LEASE_TERM',
  maxTerm: 'MAXIMUM_LEASE_TERM',
  qualityOfSpace: 'QUALITY_OF_SPACE',
  hasTerrace: 'HAS_TERRACE',
  floorNumber: 'FLOOR_NUMBER',
  suiteNumber: 'SUITE_NUMBER',
  totalNumberWorkSpacesAvailable: 'TOTAL_NUMBER_WORKSPACES_AVAILABLE',
  asking_rental_rate_monthly: 'ASKING_RENTAL_RATE_MONTHLY',
  askingPriceForSale: 'ASKING_PRICE_FOR_SALE',
  maxContiguousSpace: 'MAX_CONTIGUOUS_SPACE',
  availabilityPropertyStatus: 'AVAILABILITY_PROPERTY_STATUS',
  dateAvailable: 'DATE_AVAILABLE',
};

const MARKET_FILTER = {
  type: FilterInputType.MultiSelect,
  field: 'property.property_usage.lig_geography.market_desc',
  label: 'shared.market',
  translateOptionsRequired: false,
  dataType: DataType.Text,
  options: [],
  inputIdentifier: inputIdentifiers.marketIdentifier,
  key: AvailabilityColumns.Property_Market,
};

const SUBMARKET_FILTER = {
  type: FilterInputType.MultiSelect,
  field: 'property.property_usage.lig_geography.submarket_desc',
  label: 'shared.submarket',
  translateOptionsRequired: false,
  dataType: DataType.Text,
  options: [],
  inputIdentifier: inputIdentifiers.submarketIdentifier,
  key: AvailabilityColumns.Property_Submarket,
};

const DISTRICT_FILTER = {
  type: FilterInputType.MultiSelect,
  field: 'property.property_usage.lig_geography.district_desc',
  label: 'shared.district',
  value: [],
  translateOptionsRequired: false,
  dataType: DataType.Text,
  options: [],
  inputIdentifier: inputIdentifiers.districtIdentifier,
  key: AvailabilityColumns.Property_District,
};

const NEIGHBOURHOOD_FILTER = {
  type: FilterInputType.MultiSelect,
  field: 'property.property_usage.lig_geography.neighborhood_desc',
  placeHolder: 'marketStats.selectNeighborhood',
  label: 'shared.neighborhood',
  dataType: DataType.Text,
  value: [],
  translateOptionsRequired: false,
  options: [],
  inputIdentifier: inputIdentifiers.neighbourhoodIdentifier,
  key: AvailabilityColumns.Property_Neighborhood,
};

const MIN_SIZE = {
  key: 18434,
  type: FilterInputType.Range,
  field: 'available_space',
  label: 'shared.sizeRange',
  dataType: DataType.Numeric,
  inputIdentifier: inputIdentifiers.availableSpace,
  dataKey: FilterInputType.Range,
  value: '',
  inputType: 'number',
};

const TENURE = {
  type: FilterInputType.MultiSelect,
  field: 'available_space_condition_type_desc',
  label: 'shared.tenure',
  value: '',
  dataType: DataType.Text,
  translateOptionsRequired: false,
  options: [],
  inputIdentifier: inputIdentifiers.migTimLeasePropertyPurchase,
};

const FROM_PRICE = {
  type: FilterInputType.Range,
  field: 'asking_rental_rate_yearly',
  label: 'shared.priceRange',
  dataType: DataType.Numeric,
  dataKey: FilterInputType.Range,
  value: '',
  key: AvailabilityColumns.Terms_AskingRateYearly,
  inputType: 'number',
};

const MARKET_STATUS = {
  type: FilterInputType.MultiSelect,
  field: 'ref_space_availability_status_desc',
  label: 'shared.availabilityStatus',
  dataType: DataType.Text,
  value: ['Current', 'Available', 'Under Offer', 'Under Contract', 'Active', 'Under Negotiation'],
  translateOptionsRequired: false,
  options: [],
  inputIdentifier: inputIdentifiers.marketStatus,
  key: AvailabilityColumns.SpaceDetails_AvailabilityStatus,
};

const CBRE_INSTRUCTIONS = {
  type: FilterInputType.Select,
  field: 'cbreInstructions',
  label: 'shared.cbreInstructions',
  value: '',
  translateOptionsRequired: false,
  options: [],
  inputIdentifier: inputIdentifiers.cbreInstructions,
  disabled: false,
};

const AVAILABILITY_TYPE = {
  type: FilterInputType.MultiSelect,
  field: 'ref_availability_type_desc',
  label: 'recordsSearch.availabilityType',
  options: [],
  lookupValueKey: 'key',
  translateOptionsRequired: false,
  inputIdentifier: inputIdentifiers.availabilityType,
  dataType: DataType.Text,
  key: AvailabilityColumns.SpaceDetails_AvailabilityType,
};

const SPACE_USAGE_TYPE = {
  type: FilterInputType.MultiSelect,
  field: 'property.property_usage.ref_property_usage_type_desc',
  label: 'recordsSearch.spaceUsageType',
  options: [],
  lookupValueKey: 'key',
  translateOptionsRequired: false,
  inputIdentifier: inputIdentifiers.spaceUsageType,
  dataType: DataType.Text,
  key: AvailabilityColumns.SpaceDetails_SpaceUsageType,
};

const SPACE_USAGE_SUB_TYPE = {
  type: FilterInputType.MultiSelect,
  field: 'property.property_usage.ref_property_usage_sub_type_desc',
  label: 'recordsSearch.spaceUsageSubType',
  options: [],
  translateOptionsRequired: false,
  inputIdentifier: inputIdentifiers.spaceUsageSubType,
  dataType: DataType.Text,
  key: AvailabilityColumns.SpaceDetails_SpaceUsageSubtype,
};

const LEASE_TYPE = {
  type: FilterInputType.MultiSelect,
  field: 'ref_lease_type_desc',
  label: 'recordsSearch.leaseType',
  options: [],
  translateOptionsRequired: false,
  inputIdentifier: inputIdentifiers.leaseType,
  dataType: DataType.Text,
  key: AvailabilityColumns.SpaceDetails_LeaseType,
};

const GRADE = {
  type: FilterInputType.MultiSelect,
  field: 'ref_property_class_type_desc',
  label: 'shared.grade',
  options: [],
  lookupValueKey: 'key',
  translateOptionsRequired: false,
  inputIdentifier: inputIdentifiers.grade,
  dataType: DataType.Text,
  key: AvailabilityColumns.SpaceDetails_Grade,
};

const SMOKE_EXTRACTION_UNIT = {
  type: FilterInputType.Select,
  field: 'smoke_extraction_unit_f',
  label: 'shared.smokeExtractionUnit',
  options: [],
  translateOptionsRequired: false,
  inputIdentifier: inputIdentifiers.smokeExtractionUnit,
  dataType: DataType.Boolean,
  key: AvailabilityColumns.AvailabilitySmokeExtractionUnit,
};

const HAS_TERRACE = {
  type: FilterInputType.Select,
  field: 'terrace_f',
  label: 'shared.hasTerrace',
  options: [],
  translateOptionsRequired: false,
  inputIdentifier: inputIdentifiers.hasTerrace,
  dataType: DataType.Boolean,
  key: AvailabilityColumns.AvailabilityHasTerrace,
};

const MIN_LEASE_TERM = {
  type: FilterInputType.EqualNoCase,
  field: 'minumum_lease_term',
  label: 'shared.minTerm',
  dataType: DataType.Numeric,
  dataKey: FilterInputType.EqualNoCase,
  value: '',
  key: AvailabilityColumns.Terms_MinTerm,
};

const MAX_LEASE_TERM = {
  type: FilterInputType.EqualNoCase,
  field: 'maximum_lease_term',
  label: 'shared.maxTerm',
  dataType: DataType.Numeric,
  dataKey: FilterInputType.EqualNoCase,
  value: '',
  key: AvailabilityColumns.Terms_MaxTerm,
};

const QUALITY_OF_SPACE = {
  type: FilterInputType.MultiSelect,
  field: 'property_space_quality_type_desc',
  label: 'recordsSearch.qualityOfSpace',
  options: [],
  translateOptionsRequired: false,
  inputIdentifier: inputIdentifiers.qualityOfSpace,
  dataType: DataType.Text,
  key: AvailabilityColumns.SpaceDetails_SpaceQualityType,
};

const FLOOR_NUMBER_FILTER = {
  type: FilterInputType.EqualNoCase,
  field: 'floor_suites/floor_number',
  label: 'shared.floor',
  dataType: DataType.Text,
  options: [],
  inputIdentifier: inputIdentifiers.floorNumber,
  inputType: 'text',
  key: AvailabilityColumns.FloorSuites_FloorNumber,
};

const SUITE_NUMBER_FILTER = {
  type: FilterInputType.EqualNoCase,
  field: 'floor_suites/suite_number',
  label: 'shared.suite',
  dataType: DataType.Text,
  options: [],
  inputIdentifier: inputIdentifiers.suiteNumber,
  inputType: 'text',
  key: AvailabilityColumns.FloorSuites_Suite,
};

const TOTAL_NUMBER_WORKSPACES_AVAILABLE_FILTER = {
  key: AvailabilityColumns.AvailabilitySpaceDetails_TotalNumberofWorkspacesAvailable,
  type: FilterInputType.Range,
  field: 'total_number_workplace',
  label: 'shared.totalnumberworkspacesavailable',
  dataType: DataType.Numeric,
  dataKey: FilterInputType.Range,
  value: '',
  inputIdentifier: inputIdentifiers.totalNumberWorkSpacesAvailable,
  inputType: 'number',
};

const ASKING_RENTAL_RATE_MONTHLY_FILTER = {
  key: AvailabilityColumns.AvailabilitySpaceDetails_AskingRentalRateMonthly,
  type: FilterInputType.Range,
  field: 'asking_rental_rate_monthly',
  label: 'shared.askingrentalratemonthly',
  dataType: DataType.Numeric,
  dataKey: FilterInputType.Range,
  value: '',
  inputIdentifier: inputIdentifiers.asking_rental_rate_monthly,
  inputType: 'number',
};

const ASKING_PRICE_FOR_SALE_FILTER = {
  key: AvailabilityColumns.Terms_AskingPriceSale,
  type: FilterInputType.Range,
  field: 'asking_price_for_sale',
  label: 'shared.askingpriceforsale',
  dataType: DataType.Numeric,
  dataKey: FilterInputType.Range,
  value: '',
  inputIdentifier: inputIdentifiers.askingPriceForSale,
  inputType: 'number',
};

const MAX_CONTIGUOUS_SPACE_FILTER = {
  key: AvailabilityColumns.SpaceDetails_MaxContiguousSpace,
  type: FilterInputType.Range,
  field: 'total_contiguous_area_of_space',
  label: 'shared.maxcontiguousspace',
  dataType: DataType.Numeric,
  dataKey: FilterInputType.Range,
  value: '',
  inputIdentifier: inputIdentifiers.maxContiguousSpace,
  inputType: 'number',
};

const AVAILABILITY_PROPERTY_STATUS = {
  type: FilterInputType.MultiSelect,
  field: 'property.ref_property_status_desc',
  label: 'recordsSearch.propertyStatus',
  options: [],
  lookupValueKey: 'key',
  translateOptionsRequired: false,
  inputIdentifier: inputIdentifiers.availabilityPropertyStatus,
  dataType: DataType.Text,
  key: AvailabilityColumns.AvailabilityProperty_Status,
  customLabel: true,
};

const DATE_AVAILABLE = {
  type: FilterInputType.Range,
  field: 'date_available',
  label: 'recordsSearch.dateAvailable',
  translateOptionsRequired: false,
  inputIdentifier: inputIdentifiers.dateAvailable,
  dataType: DataType.Date,
  dataKey: FilterInputType.Range,
  inputType: 'date',
  key: AvailabilityColumns.SpaceDetails_DateAvailable,
  customLabel: true,
};

/**
 * Filters to be rendered in modal
 */
export const FILTERS = (extractData: DataObject): InputProps[] => [
  {
    ...MARKET_FILTER,
    targetField: 'property.property_usage.lig_geography.submarket_desc',
    controlOptions: {
      ...buildLevelControlOptions('market', 'market_descs'),
      payload: {
        ...MARKET_RELATED_COMMON_PAYLOAD(extractData),
      },
    },
  },
  {
    ...SUBMARKET_FILTER,
    targetField: 'property.property_usage.lig_geography.district_desc',
    controlOptions: {
      includeEmptyFilter: true,
      payloadModel: { ...getPayloadModelList(1) },
      payload: {
        ...MARKET_RELATED_COMMON_PAYLOAD(extractData),
      },
      ...buildLevelControlOptions('submarket', 'market_descs'),
    },
  },
  {
    ...DISTRICT_FILTER,
    targetField: 'property.property_usage.lig_geography.neighborhood_desc',
    controlOptions: {
      includeEmptyFilter: true,
      payloadModel: { ...getPayloadModelList(2) },
      payload: {
        ...MARKET_RELATED_COMMON_PAYLOAD(extractData),
      },
      ...buildLevelControlOptions('district', 'submarket_descs'),
    },
  },
  {
    ...NEIGHBOURHOOD_FILTER,
    controlOptions: {
      includeEmptyFilter: true,
      payload: {
        ...MARKET_RELATED_COMMON_PAYLOAD(extractData),
      },
      payloadModel: { ...getPayloadModelList(3) },
      ...buildLevelControlOptions('neighborhood', 'district_descs'),
    },
  },
  AVAILABILITY_PROPERTY_STATUS,
  SPACE_USAGE_TYPE,
  MARKET_STATUS,
  MIN_SIZE,
  FROM_PRICE,
  TENURE,
  CBRE_INSTRUCTIONS,
];

export const MARKET_RELATED_COMMON_PAYLOAD = (extractData: DataObject) => {
  return {
    id: -110993,
    global_region_descs: (extractData.globalRegionDesc || []).map(m => m.key),
    division_descs: (extractData.divisionDesc || []).map(m => m.key),
    market_descs: [],
    submarket_descs: [],
    district_descs: [],
    neighborhood_desc: [],
    local_market_area_desc: [],
  };
};

export const getPayloadModelList = (limit: number) => {
  return [
    { market_descs: 'property.property_usage.lig_geography.market_desc' },
    { submarket_descs: 'property.property_usage.lig_geography.submarket_desc' },
    { district_descs: 'property.property_usage.lig_geography.district_desc' },
  ]
    .splice(0, limit)
    .reduce((acc, curr) => ({ ...acc, ...curr }), {});
};

export const refreshGeographicalFilters = (component: FiltersBarComponent) => {
  const filters = component.getFilters();

  const dependentFields = [
    'property.property_usage.lig_geography.submarket_desc',
    'property.property_usage.lig_geography.district_desc',
    'property.property_usage.lig_geography.neighborhood_desc',
  ];

  const currentSelections = {};
  dependentFields.forEach(field => {
    currentSelections[field] = component.form.value[field] || [];
  });

  const currentMarketValue = component?.form?.value['property.property_usage.lig_geography.market_desc'];
  if (Array.isArray(currentMarketValue) && currentMarketValue?.length) {
    const marketFilter = filters?.find(f => f.field === 'property.property_usage.lig_geography.market_desc');
    const submarketFilter = filters?.find(f => f.field === 'property.property_usage.lig_geography.submarket_desc');

    if (submarketFilter?.controlOptions?.applyCascadeChanges) {
      marketFilter.value = currentMarketValue;
      submarketFilter.controlOptions.applyCascadeChanges(marketFilter, submarketFilter, component);

      setTimeout(() => {
        restoreValidSelections(component, dependentFields, currentSelections);
      }, 200);
    }
  } else {
    dependentFields.forEach(field => {
      const filterIndex = filters?.findIndex(f => f.field === field);
      if (filterIndex !== -1) {
        filters[filterIndex].options = [FILTER_EMPTY_VALUE_MULTI];
        filters[filterIndex].value = null;
        filters[filterIndex].values = [];
        if (component?.form?.controls[field]) {
          component.form.controls[field].setValue(null);
        }
      }
    });
  }
};

const restoreValidSelections = (component: FiltersBarComponent, fields: string[], previousSelections) => {
  const filters = component.getFilters();

  fields.forEach(field => {
    const filter = filters.find(f => f.field === field);
    const previousSelection = previousSelections[field];

    if (filter?.options && Array.isArray(previousSelection) && previousSelection?.length) {
      const validSelections = previousSelection.filter(selection =>
        filter.options.some(option => option.key === selection || option.value === selection)
      );

      if (validSelections?.length) {
        filter.value = validSelections;
        filter.values = validSelections;

        if (component.form.controls[field]) {
          component.form.controls[field].setValue(validSelections);
        }
      }
    }
  });
};

export const buildLevelControlOptions = (level: string, parentPayloadField: string) => {
  return {
    lookupCategory: `POST:/locations/target/${level}`,
    applyCascadeChanges: (parentFilter: InputProps, targetFilter: InputProps, component: FiltersBarComponent) => {
      const filters = component.getFilters();
      if (!parentFilter || !targetFilter) {
        return;
      }
      const validateValue = (value: any, options: InputPropOption[]) => {
        if (Array.isArray(value)) {
          return value?.filter(v => options.some(d => d.key === v || d.value === v)) || [];
        }
        return options.some(d => d.key === value || d.value === value) ? value : null;
      };
      const targetFiterIndex = filters.findIndex(filter => filter.field === parentFilter?.targetField);
      const currentValue = component.form.value[parentFilter.targetField];
      const parentValue = component.form.value[parentFilter.field];
      const controlOptions = targetFilter?.controlOptions || null;
      const payload = { ...(controlOptions.payload || {}) };
      Object.entries(controlOptions.payloadModel || {}).forEach(([targetField, sourceField]: [string, string]) => {
        payload[targetField] = (component.form.value[sourceField] || []).map(m => (!m ? '(empty)' : m));
      });
      const parentPayloadFieldValue = parentFilter.value || [];
      payload[parentPayloadField] = Array.isArray(parentPayloadFieldValue)
        ? parentPayloadFieldValue.map(m => (!m ? '(empty)' : m))
        : parentPayloadFieldValue;

      // Add property_types from space usage type selections
      const spaceUsageTypeValue = component.form.value['property.property_usage.ref_property_usage_type_desc'];
      if (Array.isArray(spaceUsageTypeValue) && spaceUsageTypeValue?.length) {
        payload.property_types = spaceUsageTypeValue.map(type => (!type ? '(empty)' : type));
      } else {
        payload.property_types = [];
      }
      component
        .getServices()
        .lookupService.getLocationsTarget(level, payload)
        .subscribe((data: InputPropOption[]) => {
          if (data && data.length > 0) {
            targetFilter.options = data;
            if (controlOptions.includeEmptyFilter) {
              targetFilter.options = [FILTER_EMPTY_VALUE_MULTI, ...data];
            }
            // Validate against targetFilter.options (not the raw `data`) - when includeEmptyFilter is
            // set, options was just prepended with FILTER_EMPTY_VALUE_MULTI (the "-" blank row) above,
            // but `data` never contains it. Validating against `data` silently strips a
            // previously-selected blank/"-" entry out of currentValue on every cascade refresh, so it
            // ends up missing from both the UI selection and the outgoing filter payload.
            const validValue =
              !parentValue || (Array.isArray(parentValue) && parentValue.length === 0)
                ? null
                : validateValue(currentValue, targetFilter.options);
            component.form.controls[parentFilter.targetField].setValue(validValue);
            filters[targetFiterIndex] = { ...targetFilter, value: validValue };
          } else {
            targetFilter.options = [FILTER_EMPTY_VALUE_MULTI];
            targetFilter.value = null;
            targetFilter.values = [];
            filters[targetFiterIndex] = { ...targetFilter };
          }
        });
    },
  };
};

export const SPAIN_FILTERS = (extractData: DataObject) => {
  return [
    {
      ...MARKET_FILTER,
      targetField: 'property.property_usage.lig_geography.submarket_desc',
      controlOptions: {
        ...buildLevelControlOptions('market', 'market_descs'),
        payload: {
          ...MARKET_RELATED_COMMON_PAYLOAD(extractData),
        },
      },
    },
    {
      ...SUBMARKET_FILTER,
      targetField: 'property.property_usage.lig_geography.district_desc',
      controlOptions: {
        includeEmptyFilter: true,
        payloadModel: { ...getPayloadModelList(1) },
        payload: {
          ...MARKET_RELATED_COMMON_PAYLOAD(extractData),
        },
        ...buildLevelControlOptions('submarket', 'market_descs'),
      },
    },
    {
      ...DISTRICT_FILTER,
      targetField: 'property.property_usage.lig_geography.neighborhood_desc',
      controlOptions: {
        includeEmptyFilter: true,
        payloadModel: { ...getPayloadModelList(2) },
        payload: {
          ...MARKET_RELATED_COMMON_PAYLOAD(extractData),
        },
        ...buildLevelControlOptions('district', 'submarket_descs'),
      },
    },
    {
      ...NEIGHBOURHOOD_FILTER,
      controlOptions: {
        includeEmptyFilter: true,
        payload: {
          ...MARKET_RELATED_COMMON_PAYLOAD(extractData),
        },
        payloadModel: { ...getPayloadModelList(3) },
        ...buildLevelControlOptions('neighborhood', 'district_descs'),
      },
    },
    AVAILABILITY_PROPERTY_STATUS,
    SPACE_USAGE_TYPE,
    SPACE_USAGE_SUB_TYPE,
    LEASE_TYPE,
    MIN_SIZE,
    ASKING_RENTAL_RATE_MONTHLY_FILTER,
    ASKING_PRICE_FOR_SALE_FILTER,
    AVAILABILITY_TYPE,
    MARKET_STATUS,
    GRADE,
    QUALITY_OF_SPACE,
    SMOKE_EXTRACTION_UNIT,
    HAS_TERRACE,
    FLOOR_NUMBER_FILTER,
    SUITE_NUMBER_FILTER,
    TOTAL_NUMBER_WORKSPACES_AVAILABLE_FILTER,
    MIN_LEASE_TERM,
    MAX_LEASE_TERM,
    MAX_CONTIGUOUS_SPACE_FILTER,
    DATE_AVAILABLE,
  ];
};

export const FILTER_EMPTY_VALUE: InputPropOption & { code?: string } = {
  key: null,
  value: null,
  name: '--',
  actualValue: null,
  code: '--',
};

/**
 * Same blank/"-" placeholder as FILTER_EMPTY_VALUE, but with '' instead of null for key/value.
 * mat-select treats a null-valued option as a "reset" trigger and never renders it as checked,
 * even when null IS present in the selected-values array - '' is falsy too (so the existing
 * `option.value || option.key || '-'` display fallback still shows "-") but is not `== null`, so
 * mat-select selects it like any other option. Use this for MultiSelect filters' blank/"-" row
 * (this is what makes it show as selected once "select all by default" includes it); keep
 * FILTER_EMPTY_VALUE (null) for single-Select filters, where null's "reset to placeholder" is
 * the wanted behavior.
 */
export const FILTER_EMPTY_VALUE_MULTI: InputPropOption & { code?: string } = {
  key: '',
  value: '',
  name: '--',
  actualValue: '',
  code: '--',
};
export enum CompanyOptionsKeyss {
  RoleDesc = 516,
  CompanyName = 501,
}

/**
 *
 * CBRE instruction filter request
 */
export const CbreInstructionsReq = (cbreInstructionsValue: string) => {
  const isCBRE = cbreInstructionsValue === 'Yes';
  if (!isCBRE) {
    return [
      {
        type: 'equalnoCaseNot',
        field: 'company_contact_role_addresses/company_name',
        values: 'cbre',
        dataType: DataType.Text,
        key: CompanyOptionsKeyss.CompanyName,
      },
      {
        type: 'equal',
        field: 'company_contact_role_addresses/role_desc',
        values: 'Listing Representative',
        dataType: DataType.Text,
        key: CompanyOptionsKeyss.RoleDesc,
      },
    ];
  }
  return {
    values: [
      {
        type: 'equal',
        field: 'company_contact_role_addresses/role_desc',
        key: CompanyOptionsKeyss.RoleDesc,
        values: 'Listing Representative',
        dataType: DataType.Text,
      },
      {
        type: isCBRE ? 'equalnocase' : 'equalnoCaseNot',
        field: 'company_contact_role_addresses/company_name',
        key: CompanyOptionsKeyss.CompanyName,
        values: 'cbre',
        dataType: DataType.Text,
      },
    ],
    type: 'group',
    field: 'company_contact_role_addresses/company_name',
    dataType: DataType.Text,
  };
};

export const FILTERS_OPTIONS_CALLS = [
  inputIdentifiers.migTimLeasePropertyPurchase,
  inputIdentifiers.marketStatus,
  inputIdentifiers.cbreInstructions,
  inputIdentifiers.availabilityType,
  inputIdentifiers.spaceUsageType,
  inputIdentifiers.spaceUsageSubType,
  inputIdentifiers.leaseType,
  inputIdentifiers.grade,
  inputIdentifiers.smokeExtractionUnit,
  inputIdentifiers.qualityOfSpace,
  inputIdentifiers.hasTerrace,
  inputIdentifiers.availabilityPropertyStatus,
];

export const enum MatchingAvailabilityTemplate {
  Id = '2',
}

export const enum AvailabilitySpaceSizeEnabled {
  Yes = 18434,
  No = 3,
}

export const defaultIDs = [
  317, 318, 319, 320, 305, 5, 6, 2, 1, 8, 3895, 18320, 18604, 19149, 3722, 3723, 18480, 18481, 17378, 17305, 206, 20,
];

/**
 * Utility to map filter config labels from columns API response
 */
export function mapFilterLabelsFromColumns(filters: InputProps[], availabilityColumns: DataObject[]): InputProps[] {
  const columnIdToHeader = availabilityColumns.reduce(
    (acc, col) => {
      if (col.columnId && col.header) acc[col.columnId] = col.header;
      return acc;
    },
    {} as Record<string, string>
  );

  return filters.map(filter => {
    const dynamicLabel = filter.key ? columnIdToHeader[filter.key] : null;
    return dynamicLabel && !filter.customLabel ? { ...filter, label: dynamicLabel } : filter;
  });
}

/**
 * Filter inputIdentifiers that are always shown regardless of API filter settings.
 * These are the geo-hierarchy filters and mandatory search criteria.
 */
/**
 * Column IDs that represent the available_space (size range) filter on the BE side.
 * key=3  (AvailabilitySpaceSizeEnabled.No) and key=17263 are both BE-side representations
 * of the size range; the FE handles this via the MIN_SIZE required filter + setRangeFilterOptions.
 * Used to skip these from optional filter rendering and to mark them as pre-selected defaults.
 */
export const AVAILABLE_SPACE_COLUMN_IDS = new Set([3, 17263]);

/**
 * Some catalog-sourced filter entries come back shaped as a raw column definition (`columnId`,
 * possibly a string) rather than the resolved IOption shape (`key` as a number) other filters
 * use - coerce to Number and check both fields.
 */
export const isPostalCodeFilter = (filter: { key?: any; columnId?: any }): boolean =>
  Number(filter?.key ?? filter?.columnId) === AvailabilityColumns.Property_PostalCode;

export const REQUIRED_FILTER_IDENTIFIERS: string[] = [
  inputIdentifiers.marketIdentifier,
  inputIdentifiers.submarketIdentifier,
  inputIdentifiers.districtIdentifier,
  inputIdentifiers.neighbourhoodIdentifier,
  inputIdentifiers.marketStatus,
  inputIdentifiers.cbreInstructions,
  inputIdentifiers.migTimLeasePropertyPurchase,
  inputIdentifiers.availableSpace,
];

/**
 * Maps a single API filter option (IOption from getGenericFilters response) to an InputProps
 * config suitable for the filter bar. Used for filters not present in the FE master catalog.
 */
function mapApiOptionToInputProps(apiOption: IOption): InputProps {
  let filterType: FilterInputType;

  // Postal Code has no lookupCategory - it's a free-text field, not a real options-backed
  // dropdown - so it's forced to a plain text input regardless of what inputType/type the API
  // reports for it.
  if (isPostalCodeFilter(apiOption)) {
    filterType = FilterInputType.EqualNoCase;
  } else if (apiOption.inputType === 'dropdown') {
    filterType = apiOption.type === 'multi' ? FilterInputType.MultiSelect : FilterInputType.Select;
  } else if (['number', 'date'].includes(apiOption.inputType)) {
    filterType = FilterInputType.Range;
  } else if (apiOption.type === 'equalnocase') {
    filterType = FilterInputType.EqualNoCase;
  } else {
    filterType = FilterInputType.Equal;
  }

  const base: InputProps = {
    key: Number(apiOption.key),
    type: filterType,
    field: apiOption.field,
    label: apiOption.value,
    dataType: apiOption.dataType,
    options: [],
    translateOptionsRequired: false,
    ...(isPostalCodeFilter(apiOption) ? { inputType: 'text' } : {}),
  };

  if (filterType === FilterInputType.Range) {
    return {
      ...base,
      dataKey: FilterInputType.Range,
      value: '',
      ...(apiOption.inputType === 'date' ? { inputType: 'date' } : {}),
    };
  }

  return base;
}

/**
 * Builds the filter list for the view-spaces-from-tim dialog using the filter settings
 * fetched from the BE API (lookup/filters?feature=SearchMatchAvailability).
 *
 * Strategy:
 * 1. Always includes the required base filters (geo hierarchy + Market Status + CBRE + Tenure).
 * 2. For each optional filter in the API response, uses the FE master catalog InputProps config
 *    when available (preserves cascading logic, lookup categories etc.), otherwise constructs
 *    a basic InputProps from the API data.
 * 3. Falls back to the full master catalog when the API returns no filters.
 *
 * @param apiFilterSections - Filter sections from the generic filters API response
 * @param extractData       - Geo data extracted from the TIM record
 * @param isEuropean        - When true, uses SPAIN_FILTERS as the master catalog (superset)
 */
export function buildFiltersFromUserSettings(
  apiFilterSections: ISearchFilterSettings[],
  extractData: DataObject,
  isEuropean: boolean
): InputProps[] {
  const masterCatalog: InputProps[] = isEuropean ? SPAIN_FILTERS(extractData) : FILTERS(extractData);

  // Flatten all filter options from the API response
  const apiFilterOptions: IOption[] = (apiFilterSections ?? []).flatMap(section => section.filters ?? []);

  if (apiFilterOptions.length === 0) {
    // No API filters returned – fall back to the full master catalog
    return masterCatalog;
  }

  // Required filters that must always appear (geo hierarchy + mandatory criteria)
  const requiredFilters = masterCatalog.filter(
    f => REQUIRED_FILTER_IDENTIFIERS.includes(f.inputIdentifier) || !f.key
  );
  const requiredKeySet = new Set(requiredFilters.map(f => f.key).filter(Boolean));

  // For each API filter option, use the master catalog config when available, else build dynamically
  const optionalFilters: InputProps[] = [];
  apiFilterOptions.forEach(apiOption => {
    const columnId = Number(apiOption.key);
    if (Number.isNaN(columnId) || columnId <= 0) return;

    // Skip available_space column IDs — rendered as a required filter, not an optional one
    if (AVAILABLE_SPACE_COLUMN_IDS.has(columnId)) return;

    // Skip if already covered by required filters
    if (requiredKeySet.has(columnId)) return;

    const masterFilter = masterCatalog.find(f => f.key === columnId);
    if (masterFilter) {
      optionalFilters.push(masterFilter);
    } else {
      // Filter not in master catalog – build from API data
      optionalFilters.push(mapApiOptionToInputProps(apiOption));
    }
  });

  return [...requiredFilters, ...optionalFilters];
}
